// export * from './storage'
export const isEqualObject = (obj1, obj2) =>
  JSON.stringify(obj1) === JSON.stringify(obj2);

export const isObjectValueEmpty = (obj) =>
  Object.values(obj).every((item) => item !== "") ? true : false;

export const checkFormError = async (inputValue, schema) => {
  try {
    const validationResult = await schema.validate(inputValue, {
      abortEarly: false,
    });
    return !!validationResult;
  } catch (error) {
    let obj = {};
    const inner = error?.inner;
    if (Array.isArray(inner) && inner.length > 0) {
      inner.forEach((vr) => {
        obj[vr.path] = vr.errors[0];
      });
    } else if (error?.path) {
      obj[error.path] = error.message;
    }
    return obj;
  }
};

export const getResposeError = (resError) => {
  let error = "";
  for (const key in resError) {
    if (Object.hasOwnProperty.call(resError, key)) {
      error =
        typeof resError[key] === "string" ? resError[key] : resError[key][0];
    }
  }
  return error;
};

export const toApiErrorMessage = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0] ?? "";
  if (typeof value === "object") {
    const first = Object.values(value).map(toApiErrorMessage).find(Boolean);
    return first ?? "";
  }
  return String(value);
};

/**
 * Maps V2 API error payloads onto form field keys.
 * @param {Record<string, string>} [fieldMap] - API field name → form field name
 * @returns {{ fieldErrors: object, generalError: string, hasFieldError: boolean }}
 */
export const mapApiErrorsToForm = (apiResponse, initialErrors, fieldMap = {}) => {
  const fieldErrors = { ...initialErrors };
  let generalError = "";
  let hasFieldError = false;

  const errorSource = apiResponse?.errors ?? apiResponse?.error;
  if (!errorSource || typeof errorSource !== "object" || Array.isArray(errorSource)) {
    return {
      fieldErrors,
      generalError:
        typeof apiResponse === "string" ? apiResponse : apiResponse?.message || "",
      hasFieldError: false,
    };
  }

  const assignField = (formKey, value) => {
    if (!(formKey in fieldErrors)) return false;
    fieldErrors[formKey] = toApiErrorMessage(value);
    return !!fieldErrors[formKey];
  };

  for (const [key, value] of Object.entries(errorSource)) {
    if (key === "non_field_errors") {
      generalError = toApiErrorMessage(value);
      continue;
    }

    if (
      key === "company_details" &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      Object.entries(value).forEach(([nestedKey, nestedVal]) => {
        if (assignField(fieldMap[nestedKey] ?? nestedKey, nestedVal)) {
          hasFieldError = true;
        }
      });
      continue;
    }

    if (assignField(fieldMap[key] ?? key, value)) hasFieldError = true;
  }

  return { fieldErrors, generalError, hasFieldError };
};

/**
 * Maps API errors to form fields and resolves a general display message.
 * @returns {{ fieldErrors: object, displayError: string, hasFieldError: boolean }}
 */
export const resolveApiFormErrors = (
  apiResponse,
  initialErrors,
  { fieldMap = {}, fallbackMessage = "" } = {}
) => {
  const { fieldErrors, generalError, hasFieldError } = mapApiErrorsToForm(
    apiResponse,
    initialErrors,
    fieldMap
  );

  let displayError = "";
  if (generalError) {
    displayError = generalError;
  } else if (!hasFieldError && apiResponse?.message) {
    displayError = apiResponse.message;
  } else if (!hasFieldError && fallbackMessage) {
    displayError = fallbackMessage;
  }

  return { fieldErrors, displayError, hasFieldError };
};

export const queryString = (params) =>
  Object.keys(params)
    .map((key) => key + "=" + params[key])
    .join("&");

export const formateDatewithJoin = (value = "", join = "-") => {
  value = new Date(value);
  return value
    ? value.getDate() +
    join +
    (value.getMonth() + 1) +
    join +
    value.getFullYear()
    : "";
};

export const getRangeArray = (start = 0, stop = 1, step = 1) => {
  const length = Math.ceil((stop - start) / step);
  return Array.from({ length }, (_, i) => i * step + start);
};

export const getYear = (date) => new Date(date).getFullYear();

export const spliceString = (
  text = "", // string
  start = 0, // index
  end = 0, // index
  join = "", // character
  from = "", // character
  to = 0 //lenght or count
) => {
  if (from !== "") {
    let ind = text.indexOf(".");
    return (
      text.slice(start, end > ind ? ind : end) +
      join +
      text.slice(ind, text.length)
    );
  } else {
    return text.slice(start, end);
  }
};

export const firstLetterCapital = (word = "") =>
  word?.length > 1
    ? word?.charAt(0).toUpperCase() + word?.slice(1)
    : word?.toUpperCase();

export const maskProtectedNumber = (phone = "") =>
  phone.replace(/\d(?=\d{3})/g, "*");

export const maskProtectedEmail = (string = "") =>
  string.replace(
    /(...)(.{1,4})(?=.*@)/g,
    (_, a, b) => a + "*".repeat(b.length)
  );

export * from "./toast_context";

export const getDisplayFileName = (selectedFiles, value, maxLength = 20) => {
  // Case 1: File(s) selected via drop/upload
  if (Array.isArray(selectedFiles) && selectedFiles[0]?.name) {
    return spliceString(selectedFiles[0].name, 0, maxLength, '', '.');
  }

  // Case 2: Direct File object provided (via props/state)
  if (value instanceof File) {
    return spliceString(value.name, 0, maxLength, '', '.');
  }

  // Case 3: String path or URL provided — extract the filename portion
  if (typeof value === 'string' && value.trim() !== '') {
    const lastPart = value.split('/').pop();
    return spliceString(lastPart, 0, maxLength);
  }

  // Fallback: No valid file info
  return '';
};
